import React, { useState, useEffect, useRef, useCallback } from 'react';

const ClientSelector = ({ clients, selectedClient, onClientSelect, onClientCreate, loading, searching, onSearch }) => {
  const [inputValue, setInputValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [newClientId, setNewClientId] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef(null);
  const newClientRef = useRef(null);
  const dropdownRef = useRef(null);
  const debounceRef = useRef(null);

  // Debounced search
  const debouncedSearch = useCallback((query) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      if (query.length >= 3 && onSearch) {
        onSearch(query);
      }
    }, 300);
  }, [onSearch]);

  // Handle input change
  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    setHighlightedIndex(-1);

    if (value.length >= 3) {
      setIsOpen(true);
      debouncedSearch(value);
    } else {
      setIsOpen(false);
    }
  };

  // Handle client selection
  const handleSelectClient = (clientId) => {
    setInputValue(clientId);
    setIsOpen(false);
    setHighlightedIndex(-1);
    onClientSelect(clientId);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen && e.key !== 'Enter') return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev < clients.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : 0);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && clients[highlightedIndex]) {
          handleSelectClient(clients[highlightedIndex]);
        } else if (inputValue.length >= 3 && clients.length === 0) {
          handleStartCreateFromSearch();
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
      default:
        break;
    }
  };

  // Enter create mode from the "+ Create New Client" button
  const handleStartCreate = () => {
    setNewClientId('');
    setIsCreating(true);
    setIsOpen(false);
  };

  // Enter create mode from the dropdown "no results" button (pre-fill with search text)
  const handleStartCreateFromSearch = () => {
    setNewClientId(inputValue.trim());
    setIsCreating(true);
  };

  // Handle create client confirmation
  const handleCreateClient = () => {
    const clientId = newClientId.trim();
    if (clientId) {
      onClientCreate(clientId);
      setInputValue(clientId);
      setNewClientId('');
      setIsCreating(false);
      setIsOpen(false);
    }
  };

  // Handle cancel create
  const handleCancelCreate = () => {
    setIsCreating(false);
    setNewClientId('');
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Update input when selected client changes externally
  useEffect(() => {
    if (selectedClient) {
      setInputValue(selectedClient);
    }
  }, [selectedClient]);

  // Clear input handler
  const handleClear = () => {
    setInputValue('');
    setIsOpen(false);
    onClientSelect('');
    inputRef.current?.focus();
  };

  const styles = {
    container: {
      position: 'relative',
    },
    inputWrapper: {
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
    },
    input: {
      width: '100%',
      padding: '0.75rem 2.5rem 0.75rem 1rem',
      border: '2px solid #e2e8f0',
      borderRadius: '8px',
      fontSize: '0.9rem',
      background: 'white',
      color: '#1e293b',
      transition: 'all 0.2s ease',
      outline: 'none',
    },
    inputFocused: {
      borderColor: '#0d9488',
      boxShadow: '0 0 0 3px rgba(13, 148, 136, 0.1)',
    },
    clearButton: {
      position: 'absolute',
      right: '10px',
      top: '50%',
      transform: 'translateY(-50%)',
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      color: '#94a3b8',
      fontSize: '1.25rem',
      padding: '4px',
      borderRadius: '4px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.2s ease',
    },
    dropdown: {
      position: 'absolute',
      top: '100%',
      left: 0,
      right: 0,
      zIndex: 1000,
      background: 'white',
      border: '2px solid #e2e8f0',
      borderRadius: '8px',
      maxHeight: '240px',
      overflowY: 'auto',
      marginTop: '4px',
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    },
    dropdownItem: {
      padding: '0.75rem 1rem',
      cursor: 'pointer',
      fontSize: '0.9rem',
      color: '#1e293b',
      transition: 'all 0.15s ease',
      borderBottom: '1px solid #f1f5f9',
    },
    dropdownItemHighlighted: {
      background: 'linear-gradient(135deg, #f0fdfa 0%, #ccfbf1 100%)',
      color: '#0f766e',
    },
    dropdownItemLast: {
      borderBottom: 'none',
    },
    emptyState: {
      padding: '1.25rem',
      textAlign: 'center',
      color: '#64748b',
    },
    emptyText: {
      fontSize: '0.875rem',
      marginBottom: '0.75rem',
    },
    searchingIndicator: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem',
      padding: '0.5rem 0',
      fontSize: '0.85rem',
      color: '#64748b',
    },
    helpText: {
      fontSize: '0.8rem',
      color: '#94a3b8',
      marginTop: '0.5rem',
      paddingLeft: '0.25rem',
    },
    selectedBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem',
      marginTop: '0.75rem',
      padding: '0.5rem 0.75rem',
      background: 'linear-gradient(135deg, #f0fdfa 0%, #ccfbf1 100%)',
      border: '1px solid #99f6e4',
      borderRadius: '6px',
      fontSize: '0.85rem',
      color: '#0f766e',
      fontWeight: '500',
    },
    checkIcon: {
      color: '#0d9488',
      fontWeight: 'bold',
    },
    createButton: {
      background: 'linear-gradient(135deg, #0d9488 0%, #0f766e 100%)',
      color: 'white',
      border: 'none',
      padding: '0.5rem 1rem',
      borderRadius: '6px',
      fontSize: '0.85rem',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    },
    cancelButton: {
      background: '#f1f5f9',
      color: '#64748b',
      border: '1px solid #e2e8f0',
      padding: '0.5rem 1rem',
      borderRadius: '6px',
      fontSize: '0.85rem',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
    },
    createConfirmBox: {
      background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
      border: '2px solid #e2e8f0',
      borderRadius: '8px',
      padding: '1rem',
    },
    createLabel: {
      fontSize: '0.875rem',
      color: '#64748b',
      marginBottom: '0.75rem',
    },
    createClientName: {
      fontWeight: '600',
      color: '#1e293b',
      fontFamily: "'Courier New', monospace",
      background: '#e2e8f0',
      padding: '0.125rem 0.5rem',
      borderRadius: '4px',
    },
    buttonGroup: {
      display: 'flex',
      gap: '0.5rem',
      marginBottom: '0.75rem',
    },
    createNewLink: {
      display: 'block',
      width: '100%',
      marginTop: '0.5rem',
      padding: '0.5rem 0.75rem',
      background: 'transparent',
      border: '1px dashed #99f6e4',
      borderRadius: '6px',
      color: '#0d9488',
      fontSize: '0.85rem',
      fontWeight: '500',
      cursor: 'pointer',
      textAlign: 'center',
      transition: 'all 0.2s ease',
    },
  };

  return (
    <div className="client-selector" ref={dropdownRef}>
      <h3>Client Selection</h3>

      {!isCreating ? (
        <div className="client-input-group">
          <div style={styles.container}>
            <div style={styles.inputWrapper}>
              <input
                ref={inputRef}
                type="text"
                style={styles.input}
                placeholder="Search client ID (min 3 chars)..."
                value={inputValue}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onFocus={(e) => {
                  e.target.style.borderColor = '#0d9488';
                  e.target.style.boxShadow = '0 0 0 3px rgba(13, 148, 136, 0.1)';
                  if (inputValue.length >= 3 && clients.length > 0) {
                    setIsOpen(true);
                  }
                }}
                onBlur={(e) => {
                  if (!selectedClient) {
                    e.target.style.borderColor = '#e2e8f0';
                    e.target.style.boxShadow = 'none';
                  } else {
                    e.target.style.borderColor = '#0d9488';
                    e.target.style.boxShadow = '0 0 0 3px rgba(13, 148, 136, 0.1)';
                  }
                }}
              />
              {inputValue && (
                <button
                  type="button"
                  onClick={handleClear}
                  style={styles.clearButton}
                  onMouseEnter={(e) => {
                    e.target.style.color = '#64748b';
                    e.target.style.background = '#f1f5f9';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.color = '#94a3b8';
                    e.target.style.background = 'none';
                  }}
                  aria-label="Clear"
                >
                  ×
                </button>
              )}
            </div>

            {/* Searching indicator */}
            {searching && (
              <div style={styles.searchingIndicator}>
                <span className="loading">Searching...</span>
              </div>
            )}

            {/* Autocomplete dropdown */}
            {isOpen && !loading && !searching && (
              <div style={styles.dropdown}>
                {clients.length > 0 ? (
                  clients.map((client, index) => (
                    <div
                      key={client}
                      onClick={() => handleSelectClient(client)}
                      onMouseEnter={() => setHighlightedIndex(index)}
                      style={{
                        ...styles.dropdownItem,
                        ...(highlightedIndex === index ? styles.dropdownItemHighlighted : {}),
                        ...(index === clients.length - 1 ? styles.dropdownItemLast : {}),
                      }}
                    >
                      {client}
                    </div>
                  ))
                ) : (
                  <div style={styles.emptyState}>
                    <div style={styles.emptyText}>
                      No clients found matching "<strong>{inputValue}</strong>"
                    </div>
                    <button
                      style={styles.createButton}
                      onClick={handleStartCreateFromSearch}
                      onMouseEnter={(e) => {
                        e.target.style.transform = 'translateY(-1px)';
                        e.target.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = 'none';
                        e.target.style.boxShadow = '0 1px 2px 0 rgba(0, 0, 0, 0.05)';
                      }}
                    >
                      + Create "{inputValue}"
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Help text */}
          {!isOpen && !selectedClient && inputValue.length > 0 && inputValue.length < 3 && (
            <div style={styles.helpText}>
              Type at least 3 characters to search
            </div>
          )}

          {/* Always-visible Create New Client option */}
          <button
            type="button"
            style={styles.createNewLink}
            onClick={handleStartCreate}
            onMouseEnter={(e) => {
              e.target.style.color = '#0f766e';
              e.target.style.background = '#f0fdfa';
            }}
            onMouseLeave={(e) => {
              e.target.style.color = '#0d9488';
              e.target.style.background = 'transparent';
            }}
          >
            + Create New Client
          </button>

          {/* Selected client indicator */}
          {selectedClient && (
            <div style={styles.selectedBadge}>
              <span style={styles.checkIcon}>✓</span>
              <span>Selected: <strong>{selectedClient}</strong></span>
            </div>
          )}
        </div>
      ) : (
        <div className="client-input-group">
          <div style={styles.createConfirmBox}>
            <div style={styles.createLabel}>
              Create new client for testing / demo:
            </div>

            <input
              ref={newClientRef}
              type="text"
              style={styles.input}
              placeholder="Enter new client ID (e.g., demo_client_001)"
              value={newClientId}
              onChange={(e) => setNewClientId(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleCreateClient();
                } else if (e.key === 'Escape') {
                  handleCancelCreate();
                }
              }}
              autoFocus
            />

            <div style={styles.buttonGroup}>
              <button
                style={{
                  ...styles.createButton,
                  opacity: !newClientId.trim() ? 0.5 : 1,
                  cursor: !newClientId.trim() ? 'not-allowed' : 'pointer',
                }}
                onClick={handleCreateClient}
                disabled={!newClientId.trim()}
                onMouseEnter={(e) => {
                  if (newClientId.trim()) {
                    e.target.style.transform = 'translateY(-1px)';
                    e.target.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'none';
                  e.target.style.boxShadow = '0 1px 2px 0 rgba(0, 0, 0, 0.05)';
                }}
              >
                Confirm Create
              </button>

              <button
                style={styles.cancelButton}
                onClick={handleCancelCreate}
                onMouseEnter={(e) => {
                  e.target.style.background = '#e2e8f0';
                  e.target.style.borderColor = '#cbd5e1';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = '#f1f5f9';
                  e.target.style.borderColor = '#e2e8f0';
                }}
              >
                Cancel
              </button>
            </div>

            <div style={styles.helpText}>
              Client ID should be unique (e.g., patient_001, demo_client_123)
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientSelector;
